/**
 * StatCard Component
 *
 * Composite stat card for dashboard header with:
 * - Primary count with semantic coloring
 * - Descriptive label
 * - TrendIndicator for period comparison
 * - Muted state when count is 0
 * - Entrance and attention animations
 *
 * Design Compliance:
 * - Polaris spacing scale (gap values)
 * - Semantic colors with purpose (monitored=purple, critical=red, warning=amber, healthy=green)
 * - Equal card sizing with consistent layout
 * - Accessible button interactions
 * - Responsive design support
 */

import type { RefObject } from 'react';
import { Icon, type IconSource } from '@shopify/polaris';
import { TrendIndicator, type TrendData } from './TrendIndicator';
import type { StatCardType } from './StatCardActions';

export interface StatCardProps {
  /** Unique identifier matching semantic type */
  id: StatCardType;
  /** Display label (e.g., "Critical", "Warning") */
  label: string;
  /** Primary count value */
  value: number;
  /** Optional icon to display next to the label */
  icon?: IconSource;
  /** Optional trend data for period comparison */
  trend?: TrendData;
  /** Tab index for navigation target */
  tabIndex: number;
  /** Click handler for card selection */
  onClick?: (tabIndex: number, ref?: RefObject<HTMLDivElement>) => void;
  /** Optional ref for scroll targeting */
  scrollRef?: RefObject<HTMLDivElement>;
}

/**
 * StatCard - Enhanced stat card with backdrop and trends
 */
export function StatCard({
  id,
  label,
  value,
  icon,
  trend,
  tabIndex,
  onClick,
  scrollRef,
}: StatCardProps) {
  const isEmpty = value === 0;
  
  // Determine if trend should invert tone (down is good for critical/warning alerts)
  const shouldInvertTone = id === 'critical' || id === 'warning';

  // Build dynamic class list for animations and states
  const cardClasses = [
    'dashboard-stat-card',
    `dashboard-stat-card--${id}`,
    'stat-badge-enter',
    // Muted state when count is 0
    isEmpty && 'dashboard-stat-card--empty',
    // Attention animation for critical/warning with active alerts
    id === 'critical' && value > 0 && 'stat-badge-attention--critical',
    id === 'warning' && value > 0 && 'stat-badge-attention--warning',
  ].filter(Boolean).join(' ');

  const handleClick = () => {
    onClick?.(tabIndex, scrollRef);
  };

  return (
    <button
      type="button"
      className={cardClasses}
      onClick={handleClick}
      aria-label={`${label}: ${value}. Click to view details.`}
    >
      {/* Content layer */}
      <div className="dashboard-stat-card__content">
        {/* Main info section */}
        <div className="dashboard-stat-card__info">
          <div className="dashboard-stat-card__primary">
            <span className="dashboard-stat-card__count">{value}</span>
            {/* Trend indicator next to count */}
            {trend && (
              <TrendIndicator
                value={trend.value}
                direction={trend.direction}
                period={trend.period}
                invertTone={shouldInvertTone}
                size="small"
              />
            )}
          </div>
          <span className="dashboard-stat-card__label">
            {label}
            {icon && (
              <span className="dashboard-stat-card__label-icon">
                <Icon source={icon} />
              </span>
            )}
          </span>
        </div>
      </div>
    </button>
  );
}

export default StatCard;
